import { useEffect, useMemo, useRef } from 'react'
import { useFormContext, useFormState, useWatch } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import { buildErrorMessage } from '@constants'
import { AxiosError } from 'axios'
import { cloneDeep, find } from 'lodash-es'
import { hydrateCrossSectionForCalcElementChecksRequest } from '@editor/pages/Results2/components/Results2DetailPane/utils'
import { useMutation } from '@tanstack/react-query'
import { useSnackbar } from '@ui/snackbar'
import { GetCalcChecksForElement, getCalcChecksForElementRequest2 } from '@queries'
import { CheckSettingsSectionKey } from '../constants'
import { CheckSettingsFormValues, MultiEditCheckSettingsElement } from '../types'

/** Shared mutation key so a section can disable its submit while its checks recompute. */
export const checkSettingsRecomputeKey = (
  projectId: string | undefined,
  sectionKey: CheckSettingsSectionKey,
): unknown[] => [projectId, 'updateChecksMultiEditCheckSettings', sectionKey]

interface Params<SharedSettings> {
  /** Distinguishes this section's recompute mutation from the other tabs'. */
  sectionKey: CheckSettingsSectionKey
  /** Applies the staged settings to a member — the same merge the save uses. */
  mergeSettings: (existing: SettingsOnMember, shared: SharedSettings) => SettingsOnMember
  /**
   * Blocks the recompute while the staged settings are incomplete or would
   * produce settings the backend rejects for one of the members.
   */
  isFormValid?: (
    settings: SharedSettings | undefined,
    elements: MultiEditCheckSettingsElement[],
  ) => boolean
}

interface Result {
  isUpdatingChecks: boolean
  /**
   * Selected elements the live recompute cannot preview at all (no resolvable
   * cross section) — their row keeps showing whatever was computed before the
   * panel opened, which the caller should mark as not live.
   */
  unresolvedElementCount: number
}

/**
 * Recomputes every selected member's checks live while the staged shared
 * settings change, writing the results back onto the form's `elements` so the
 * section's utilization table previews the effect of a change before saving it.
 * Must live inside the section's form.
 */
const useLiveCheckRecompute = <SharedSettings = SettingsOnMember>({
  sectionKey,
  mergeSettings,
  isFormValid,
}: Params<SharedSettings>): Result => {
  const { projectId } = useParams()
  const { enqueueSnackbar } = useSnackbar()

  const { getValues, setValue } = useFormContext<CheckSettingsFormValues<SharedSettings>>()
  const elements = useWatch<CheckSettingsFormValues<SharedSettings>>({
    name: 'elements',
  }) as CheckSettingsFormValues['elements']
  const sharedSettings = useWatch<CheckSettingsFormValues<SharedSettings>>({
    name: 'sharedSettings',
  }) as SharedSettings | undefined

  // The live recompute is what produces the checks, so it must not be gated on
  // preexisting checks — only on having an element whose cross section resolves
  // (the mutation skips the rest).
  const hasResolvableElements = useMemo(
    () => elements.some(element => !!element.crossSection),
    [elements],
  )
  const unresolvedElementCount = useMemo(
    () => elements.filter(element => !element.crossSection).length,
    [elements],
  )

  const abortController = useRef(new AbortController())

  const { mutateAsync: updateChecks, isLoading: isUpdatingChecks } = useMutation({
    mutationKey: checkSettingsRecomputeKey(projectId, sectionKey),
    mutationFn: async ({ signal }: { signal: AbortSignal }) => {
      const currentSharedSettings = getValues('sharedSettings') as SharedSettings | undefined
      const currentElements = getValues('elements') as CheckSettingsFormValues['elements']
      if (!currentSharedSettings) return []
      if (isFormValid && !isFormValid(currentSharedSettings, currentElements)) return []

      const data: { elementGuid: string; response: CrossSectionSuggestion }[] = []
      for (const element of currentElements) {
        if (!element.crossSection) continue

        const requestData: GetCalcChecksForElement = {
          element_guid: element.guid,
          crossSection: hydrateCrossSectionForCalcElementChecksRequest(element.crossSection),
          settings: mergeSettings(element.checkSettings, currentSharedSettings),
          supporting_elements_cs_config: element.supportingElementCSConfig,
        }

        const response = await getCalcChecksForElementRequest2.request(projectId as string, {
          requestData,
          options: { signal },
        })
        data.push({ elementGuid: element.guid, response: cloneDeep(response) })
      }

      return currentElements.map(element => {
        const elementResponse = find(data, item => item.elementGuid === element.guid)
        return elementResponse
          ? { ...element, checks: elementResponse.response.checks as CombinedPositionCheck[] }
          : element
      })
    },
    onSuccess: (data: CheckSettingsFormValues['elements']) => {
      if (data.length === 0) return
      setValue('elements', data)
    },
    onError: (error: AxiosError) => {
      if (error.code === 'ERR_CANCELED' || error.message === 'canceled') return
      enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Aktualisieren der Nachweise'), {
        variant: 'error',
      })
    },
  })

  const { dirtyFields } = useFormState<CheckSettingsFormValues<SharedSettings>>()

  const formIsValid = isFormValid ? isFormValid(sharedSettings, elements) : true

  useEffect(() => {
    if (dirtyFields.sharedSettings && hasResolvableElements && formIsValid) {
      abortController.current.abort()
      abortController.current = new AbortController()
      updateChecks({ signal: abortController.current.signal })
    }
  }, [sharedSettings, dirtyFields.sharedSettings, hasResolvableElements, formIsValid, updateChecks])

  return { isUpdatingChecks, unresolvedElementCount }
}

export default useLiveCheckRecompute
